import { skipToken, useQuery } from '@tanstack/react-query'
import { sessionKeys } from '../lib/queryKeys/session'

// Before the session resolves (or after a failed fetch leaves no data) readers
// see an empty user, so `Boolean(user.id)` is false, as with the old Redux slice.
const EMPTY_USER = {}
const identity = (user) => user

/**
 * Read the signed-in user.
 *
 * The user is server state in the React Query cache under
 * `sessionKeys.currentUser()`. This hook only reads it: the App root's session
 * bootstrap and the login form write it through `lib/currentUserSession`, so
 * mounting a reader never issues `GET /api/user`. Every consumer goes through
 * here so the backing store stays in one place.
 *
 * Re-renders follow the selected value: React Query structurally shares the
 * `select` result, so a consumer re-renders only when its slice changes.
 *
 * @template T
 * @param {((user: Record<string, any>) => T) | null} [select] narrow the
 *   subscription to one field, e.g. `useCurrentUser((user) => user?.account?.id)`.
 *   Pass `null` to keep the whole user.
 * @param {(left: T, right: T) => boolean} [equalityFn] accepted for call-site
 *   compatibility and ignored; structural sharing already keeps equal results
 *   referentially stable.
 * @returns {T}
 */
export default function useCurrentUser(select) {
  const pick = select || identity
  const { data } = useQuery({
    queryKey: sessionKeys.currentUser(),
    // Read-only observer: the session bootstrap and login write this key.
    queryFn: skipToken,
    staleTime: Infinity,
    select: pick,
  })
  return data === undefined ? pick(EMPTY_USER) : data
}
